import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getProducts, getCategories, getPortada } from '../services/api';
import ProductCard from '../components/product/ProductCard';
import CategoryHero from '../components/category/CategoryHero';
import CategoryCard from '../components/category/CategoryCard';
import ProductComparator from '../components/product/ProductComparator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tv, Wind, UtensilsCrossed, Fan, Speaker, Grid3x3, ArrowLeftRight, Search, Loader2 } from 'lucide-react';

const iconMap = {
  Grid3x3: Grid3x3,
  Tv: Tv,
  Wind: Wind,
  UtensilsCrossed: UtensilsCrossed,
  Fan: Fan,
  Speaker: Speaker
};

const Catalog = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  
  // Read category from URL query string
  const searchParams = new URLSearchParams(location.search);
  const categoryFromUrl = searchParams.get('category');
  
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl || 'todos');
  const [showComparator, setShowComparator] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [portada, setPortada] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Update selected category when URL changes
  useEffect(() => {
    if (categoryFromUrl && categoryFromUrl !== selectedCategory) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [categoryFromUrl]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const categoriesData = await getCategories();
        setCategories(categoriesData);
        
        // Try to load portada, but don't fail if it doesn't exist
        try {
          const portadaData = await getPortada('catalog');
          setPortada(portadaData);
        } catch (portadaError) {
          console.log('Portada catalog not found, using default');
          setPortada(null);
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Error cargando datos');
      }
    };
    fetchInitialData();
  }, []);

  // Fetch products when category changes
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const data = await getProducts(selectedCategory === 'todos' ? null : selectedCategory);
        setProducts(data);
        setError(null);
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Error cargando productos');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory]);

  // Filter products by search query
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.brand.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Get categories excluding "todos" for the discover view
  const mainCategories = categories.filter(cat => cat.id !== 'todos');

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setSearchQuery('');
    setShowComparator(false); // Close comparator when changing category
  };

  return (
    <div className="min-h-screen">
      {/* Hero Grande de Catálogo */}
      {portada && (
        <div 
          className="relative min-h-[60vh] flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: `url(${portada.imageUrl})`,
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          
          {/* Content */}
          <div className="relative z-10 container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {portada.title}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
              {portada.subtitle}
            </p>
          </div>
        </div>
      )}

      {/* Contenido del Catálogo */}
      <div className="container mx-auto px-4 py-12">
        {/* Error State */}
        {error && (
          <div className="text-center py-10">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>
              Reintentar
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading && selectedCategory !== 'todos' && (
          <div className="text-center py-20">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground mt-4">Cargando productos...</p>
          </div>
        )}

        {/* Discover View - Show when no category selected */}
        {!loading && selectedCategory === 'todos' ? (
          <div className="max-w-7xl mx-auto">
            {/* Grid: 3 columns on desktop, 2 on tablet, 1 on mobile */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 place-items-center">
              {mainCategories.map(category => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  language={language}
                  onClick={() => handleCategoryClick(category.id)}
                />
              ))}
            </div>
          </div>
        ) : !loading && (
          <>
            {/* Category Filter & Search Bar */}
            <div className="mb-12 space-y-6">
              {/* Search Bar */}
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Buscar productos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 pr-4 py-6 text-lg rounded-full border-2 focus:border-primary transition-all"
                  />
                </div>
              </div>

              {/* Category Tabs */}
              <div className="flex justify-between items-center">
                <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="flex-1">
                  <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto gap-2 bg-muted/50 p-2">
                    {categories.map(category => {
                      const IconComponent = iconMap[category.icon];
                      return (
                        <TabsTrigger 
                          key={category.id} 
                          value={category.id}
                          className="flex items-center gap-2 px-4 py-2"
                        >
                          {IconComponent && <IconComponent className="h-4 w-4" />}
                          {category.name[language]}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </Tabs>
                
                {selectedCategory !== 'todos' && filteredProducts.length > 1 && (
                  <Button
                    variant="outline"
                    className="ml-4 flex items-center gap-2"
                    onClick={() => setShowComparator(true)}
                  >
                    <ArrowLeftRight className="h-4 w-4" />
                    Comparar modelos
                  </Button>
                )}
              </div>
            </div>

            {/* Category Hero */}
            <CategoryHero category={selectedCategory} />

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  currentCategory={selectedCategory !== 'todos' ? selectedCategory : null}
                />
              ))}
            </div>

            {/* Empty State */}
            {filteredProducts.length === 0 && (
              <div className="text-center py-20">
                <p className="text-muted-foreground text-lg">
                  {searchQuery 
                    ? `No se encontraron productos con "${searchQuery}"`
                    : 'No hay productos en esta categoría'}
                </p>
                {searchQuery && (
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setSearchQuery('')}
                  >
                    Limpiar búsqueda
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Product Comparator */}
      <ProductComparator
        isOpen={showComparator}
        onClose={() => setShowComparator(false)}
        initialProduct={filteredProducts[0]}
      />
    </div>
  );
};

export default Catalog;
